import { useEffect, useState } from "react";
import { api, BRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ExpenseDialog from "@/components/ExpenseDialog";
import PaymentsDialog from "@/components/PaymentsDialog";
import { HandCoins, Pencil, CreditCard } from "lucide-react";

const statusMeta = (e) => {
  const overdue = e.status !== "paid" && new Date(e.date) < new Date(new Date().toDateString());
  if (e.status === "paid") return { label: "Pago", cls: "bg-primary hover:bg-primary text-primary-foreground" };
  if (overdue) return { label: e.status === "partial" ? "Atrasado (Parcial)" : "Atrasado", cls: "bg-destructive hover:bg-destructive text-white" };
  if (e.status === "partial") return { label: "Parcial", cls: "bg-amber-500 hover:bg-amber-500 text-white" };
  return { label: "A pagar", cls: "" };
};

export default function Pagamentos() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [hidePaid, setHidePaid] = useState(false);
  const [month, setMonth] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/expenses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const isOverdue = (e) => e.status !== "paid" && new Date(e.date) < new Date(new Date().toDateString());

  const filtered = items.filter((e) => {
    const byTab = filter === "all" || (filter === "pending" && e.status !== "paid") || (filter === "paid" && e.status === "paid") || (filter === "overdue" && isOverdue(e));
    const byPaid = !hidePaid || e.status !== "paid";
    const byMonth = !month || (e.date || "").slice(0, 7) === month;
    return byTab && byPaid && byMonth;
  });

  const totalRemaining = filtered.reduce((a, e) => a + (e.remaining || 0), 0);

  return (
    <div className="fade-up space-y-6">
      <div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Pagamentos</h1>
        <p className="text-muted-foreground mt-1">Todas as parcelas — pagas, a pagar, parciais e atrasadas</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="payments-filter-tabs">
            <TabsTrigger value="all" data-testid="pfilter-all">Todos</TabsTrigger>
            <TabsTrigger value="pending" data-testid="pfilter-pending">A pagar</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="pfilter-overdue">Atrasados</TabsTrigger>
            <TabsTrigger value="paid" data-testid="pfilter-paid">Pagos</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Mês</label>
          <Input type="month" data-testid="payments-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full sm:w-44" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={hidePaid} onCheckedChange={setHidePaid} data-testid="hide-paid-switch" /> Ocultar pagos
        </label>
        <div className="lg:ml-auto text-sm text-muted-foreground" data-testid="payments-total">
          {filtered.length} parcela(s) · Falta <span className="font-semibold text-foreground tnum">{BRL(totalRemaining)}</span>
        </div>
      </div>

      <Card className="border-border/60 shadow-none rounded-md overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="payments-empty">
            <CreditCard className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" aria-hidden />
            Nenhuma parcela para os filtros atuais.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parcela</TableHead>
                <TableHead>Cliente / Contrato</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead className="text-right">Valor / Pago</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => {
                const sm = statusMeta(e);
                return (
                  <TableRow key={e.id} data-testid={`payment-row-${e.id}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {e.client_name || "—"}{e.contract_title ? <span className="block text-xs">{e.contract_title}</span> : null}
                    </TableCell>
                    <TableCell className={isOverdue(e) ? "text-destructive font-medium" : ""}>{new Date(e.date).toLocaleDateString("pt-BR")}</TableCell>
                    <TableCell className="text-right">
                      <div className="font-semibold tnum">{BRL(e.value)}</div>
                      {e.paid > 0 && e.status !== "paid" && <div className="text-xs text-muted-foreground tnum">Pago {BRL(e.paid)} · Falta {BRL(e.remaining)}</div>}
                    </TableCell>
                    <TableCell><Badge className={`font-normal ${sm.cls}`} variant={sm.cls ? "default" : "secondary"} data-testid={`payment-status-${e.id}`}>{sm.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Pagamentos" data-testid={`pay-${e.id}`} onClick={() => setPayFor(e)}><HandCoins className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar parcela" data-testid={`edit-parcela-${e.id}`} onClick={() => setEditing(e)}><Pencil className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <PaymentsDialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)} expense={payFor} onSaved={load} />
      <ExpenseDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} expense={editing} />
    </div>
  );
}
